import React from 'react';

// One section shell for the brief detail page (restructure §3/§4).
//
// The page had FOUR container treatments doing the same job — header + hairline
// rule, white card, card list, and a bare list on the page background — so two
// containerless sections sitting next to each other visually merged into one,
// and the page read as a continuous scroll with no landmarks.
//
// This is the only shell. The GAP between cards on the sage background is the
// separator, which is why there are no standalone hairline rules here: a rule
// between two cards is a second separator doing the first one's job.
//
// TWO HEADING LEVELS, no third (§4):
//
//   Section     caps, wide tracking, in the card header — the four top-level
//               sections only. That's what <Section title> renders.
//   Subsection  sentence case, semibold, no rule — everything nested inside.
//               That's <Subsection>.
//
// Before this, "The Brief", "Final Supplier Quote" and "Final Cost" used a
// small sentence-case label while "BRIEF INFORMATION" and "ACTIVITY" used caps,
// despite being peers. Anything that was a peer is now a Section; anything
// genuinely nested is a Subsection.

const FONT = "font-['Metropolis']";

export default function Section({
  id, title, count, actions, children, className = '', bodyClassName = '',
}) {
  return (
    <section
      id={id}
      // scroll-margin so the section nav doesn't park a heading underneath the
      // pinned page header when it scrolls to it.
      className={`bg-white border border-line rounded-2xl overflow-hidden scroll-mt-24 ${className}`}>
      {title && (
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-line bg-[#FBFCF6]">
          <h2 className={`${FONT} text-[11px] font-bold tracking-[0.18em] uppercase text-fusion-forest`}>
            {title}
            {count != null && (
              <span className={`${FONT} ml-2 text-[11px] font-semibold tracking-normal normal-case text-[#51534A]`}>
                {count}
              </span>
            )}
          </h2>
          {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
        </div>
      )}
      <div className={`p-5 ${bodyClassName}`}>{children}</div>
    </section>
  );
}

// The second and only other level. No rule under it — the rule is what made
// subsections read as peers of the section headings.
export function Subsection({ title, hint, children, className = '' }) {
  return (
    <div className={className}>
      {title && (
        <h3 className={`${FONT} text-[13px] font-semibold text-ink mb-1`}>{title}</h3>
      )}
      {hint && <p className={`${FONT} text-[12px] text-[#51534A] mb-2 leading-snug`}>{hint}</p>}
      {children}
    </div>
  );
}

// The four top-level sections, and the order they appear in for a given brief
// status (§5/§6).
//
// An awarded or closed brief and a live one need different things at the top:
// once it's decided, the outcome is the answer to "what happened here?", and
// the brief itself becomes reference. Nothing is hidden — only reordered.
export const BRIEF_SECTIONS = [
  { id: 'section-brief', title: 'The brief' },
  { id: 'section-responses', title: 'Responses' },
  { id: 'section-outcome', title: 'Outcome' },
  { id: 'section-activity', title: 'Activity' },
];

export function sectionOrderFor(status, hasAward) {
  const settled = status === 'closed' || hasAward;
  return settled
    ? ['section-outcome', 'section-responses', 'section-brief', 'section-activity']
    : ['section-brief', 'section-responses', 'section-outcome', 'section-activity'];
}
